import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { NeedTenant } from "../components/layout/AppShell";

const EMPTY = { name: "", username: "", password: "", role: "cashier", branch_id: "", email: "", hourly_rate: "" };

export default function StaffPage() {
  const { user, tenant, permissions, activeTenantId } = useAuth();
  const canManageStaff = user.role === "system_admin" || !!permissions.manage_staff;
  const canManageBranches = user.role === "system_admin" || !!permissions.manage_branches;
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [branchName, setBranchName] = useState("");
  const [branchAddr, setBranchAddr] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: b }] = await Promise.all([api.get("/staff"), api.get("/branches")]);
      setStaff(s);
      setBranches(b);
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, activeTenantId]);

  const branchNameOf = (id) => branches.find((b) => b.branch_id === id)?.branch_name || "—";

  const openAdd = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, username: s.username || "", password: "", role: s.role, branch_id: s.branch_id || "", email: s.email || "", hourly_rate: s.hourly_rate ?? "" });
    setFormOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, branch_id: form.branch_id || null, email: form.email || null,
          hourly_rate: form.hourly_rate === "" ? null : Number(form.hourly_rate) };
        if (form.password) payload.password = form.password;
        await api.put(`/staff/${editing.user_id}`, payload);
        toast.success("Staff updated");
      } else {
        await api.post("/staff", { ...form, hourly_rate: Number(form.hourly_rate) || 0, branch_id: form.branch_id || null, email: form.email || null });
        toast.success("Staff account created");
      }
      setFormOpen(false);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const addBranch = async (e) => {
    e.preventDefault();
    try {
      await api.post("/branches", { branch_name: branchName, location_address: branchAddr || null });
      toast.success("Branch added");
      setBranchName(""); setBranchAddr("");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Staff &amp; Branches</h1>
          <p className="text-sm text-slate-500">
            {staff.length} of {tenant?.max_staff_allowed ?? 5} staff seats used
          </p>
        </div>
        <div className="flex-1" />
        {canManageStaff ? (
          <Button onClick={openAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="staff-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Staff
          </Button>
        ) : (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="staff-permission-notice">
            Staff management permission not granted to your role — contact the platform admin.
          </p>
        )}
      </div>

      <Tabs defaultValue="staff">
        <TabsList>
          <TabsTrigger value="staff" data-testid="staff-tab">Staff</TabsTrigger>
          <TabsTrigger value="branches" data-testid="branches-tab">Branches</TabsTrigger>
        </TabsList>

        <TabsContent value="staff" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((s) => (
                  <TableRow key={s.user_id}>
                    <TableCell className="font-medium text-slate-900">{s.name}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{s.username || s.email}</TableCell>
                    <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold capitalize">{s.role}</span></TableCell>
                    <TableCell className="text-sm text-slate-600">{branchNameOf(s.branch_id)}</TableCell>
                    <TableCell className="text-right">
                      {canManageStaff && (
                        <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.user_id}`}>
                          <Pencil className="h-4 w-4 text-slate-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {staff.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-sm text-slate-400">No staff yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="branches" className="mt-4 space-y-4">
          {!tenant?.multi_branch_enabled && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Multi-branch is disabled for this tenant — limited to 1 location. A platform admin can enable it in the Admin Console.
            </p>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map((b) => (
              <div key={b.branch_id} className="bg-white border border-slate-200 rounded-xl p-5 space-y-1">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <p className="font-semibold text-slate-900">{b.branch_name}</p>
                </div>
                <p className="text-xs text-slate-500">{b.location_address || "No address set"}</p>
              </div>
            ))}
          </div>
          <form onSubmit={addBranch} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-wrap items-end gap-3">
            <div className="space-y-1.5 flex-1 min-w-40">
              <Label>Branch name</Label>
              <Input value={branchName} onChange={(e) => setBranchName(e.target.value)} placeholder="e.g. Annex" required data-testid="branch-name-input" />
            </div>
            <div className="space-y-1.5 flex-1 min-w-40">
              <Label>Address (optional)</Label>
              <Input value={branchAddr} onChange={(e) => setBranchAddr(e.target.value)} placeholder="Street, City" data-testid="branch-address-input" />
            </div>
            <Button type="submit" className="bg-slate-900" data-testid="branch-add-btn" disabled={!canManageBranches || (!tenant?.multi_branch_enabled && branches.length >= 1)}>
              <Plus className="h-4 w-4 mr-1.5" /> Add Branch
            </Button>
          </form>
        </TabsContent>
      </Tabs>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Staff" : "Add Staff"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="staff-name-input" />
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label>Username</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required data-testid="staff-username-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{editing ? "Reset password (leave blank to keep)" : "Password"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} data-testid="staff-password-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="cashier">Cashier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Branch</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="staff-branch-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.branch_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Email <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Hourly rate (₱/hr, for payroll)</Label>
                <Input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="staff-hourly-rate-input" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-slate-900" data-testid="staff-save-btn">{editing ? "Save changes" : "Create account"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
